import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type GenerateVendorPoResponse,
  type CommitVendorPoResponse,
  type PatchVendorPoResponse,
  type VendorPurchaseOrder,
  type VendorPoShipToMode,
} from '@/lib/api';
import { useModalDismiss } from '@/lib/use-modal-dismiss';

interface Props {
  open: boolean;
  onClose: () => void;
  rfqLineItemId: string;       // li_...
  rfqId: string;               // rfq_... (cache invalidation)
  /** The vendor quote being purchased (winner). */
  vendorQuotePublicId: string; // vq_...
  /** Optional link to the accepted customer quote that triggered this PO. */
  clientQuotePublicId?: string;
  /** Defaults for the modal — read off the line / vendor quote / customer. */
  defaultQty: number;
  vendorNameRaw: string;
  vendorUnitPriceCents: number;
  currency: string;
  /** Customer hint for drop-ship prefill (legalName + country). */
  customerName?: string | null;
  customerCountryCode?: string | null;
  /** Default cert label (e.g. "FAA Form 8130-3") so the broker doesn't retype. */
  defaultCertLabel?: string | null;
}

const CURRENCY_SYMBOL: Record<string, string> = {
  USD: '$', EUR: '€', GBP: '£', CAD: 'CA$', AUD: 'A$', JPY: '¥', CHF: 'CHF ', AED: 'AED ', SGD: 'S$',
};
function fmtMoney(cents: number, currency: string): string {
  const sym = CURRENCY_SYMBOL[currency] ?? `${currency} `;
  if (currency === 'JPY') return `${sym}${Math.round(cents / 100).toLocaleString('en-US')}`;
  return `${sym}${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function VendorPoModal({
  open, onClose, rfqLineItemId, rfqId, vendorQuotePublicId, clientQuotePublicId,
  defaultQty, vendorNameRaw, vendorUnitPriceCents, currency,
  customerName, customerCountryCode, defaultCertLabel,
}: Props) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const dismiss = useModalDismiss(onClose);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const [qty, setQty] = useState<number>(defaultQty);
  const [shipToMode, setShipToMode] = useState<VendorPoShipToMode>('customer_direct');
  const [shipToText, setShipToText] = useState<string>(
    customerName
      ? `${customerName}${customerCountryCode ? `\n${customerCountryCode}` : ''}\n[enter full ship-to address]`
      : '[enter full ship-to address]',
  );
  const [paymentTerms, setPaymentTerms] = useState<string>('Per vendor quote / standing agreement');
  const [certsRequired, setCertsRequired] = useState<string>(
    defaultCertLabel ? `${defaultCertLabel} must accompany the shipment` : '',
  );
  const [requestedDeliveryDate, setRequestedDeliveryDate] = useState<string>(''); // yyyy-mm-dd
  const [notes, setNotes] = useState<string>('');

  const [result, setResult] = useState<GenerateVendorPoResponse | null>(null);
  const [saved, setSaved] = useState<VendorPurchaseOrder | null>(null);

  useEffect(() => {
    if (!open) {
      setQty(defaultQty);
      setShipToMode('customer_direct');
      setShipToText(customerName
        ? `${customerName}${customerCountryCode ? `\n${customerCountryCode}` : ''}\n[enter full ship-to address]`
        : '[enter full ship-to address]');
      setPaymentTerms('Per vendor quote / standing agreement');
      setCertsRequired(defaultCertLabel ? `${defaultCertLabel} must accompany the shipment` : '');
      setRequestedDeliveryDate('');
      setNotes('');
      setResult(null);
      setSaved(null);
    }
  }, [open, defaultQty, customerName, customerCountryCode, defaultCertLabel]);

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['vendor-purchase-orders'] });
    qc.invalidateQueries({ queryKey: ['rfq', rfqId] });
    qc.invalidateQueries({ queryKey: ['rfqs'] });
  }

  function buildBody() {
    return {
      rfqLineItemPublicId: rfqLineItemId,
      vendorQuotePublicId,
      clientQuotePublicId,
      qty,
      shipToMode,
      shipToText,
      paymentTerms: paymentTerms.trim() || undefined,
      certsRequired: certsRequired.trim() || undefined,
      requestedDeliveryDate: requestedDeliveryDate
        ? new Date(requestedDeliveryDate + 'T00:00:00Z').toISOString()
        : undefined,
      notes: notes.trim() || undefined,
    };
  }

  const generate = useMutation({
    mutationFn: async () => {
      return api<GenerateVendorPoResponse>('/api/vendor-purchase-orders/generate', {
        method: 'POST',
        tokenGetter: () => getToken(),
        body: JSON.stringify(buildBody()),
      });
    },
    onSuccess: (data) => { setResult(data); setSaved(null); },
  });

  const save = useMutation({
    mutationFn: async () => {
      return api<CommitVendorPoResponse>('/api/vendor-purchase-orders', {
        method: 'POST',
        tokenGetter: () => getToken(),
        idempotencyKey: crypto.randomUUID(),
        body: JSON.stringify(buildBody()),
      });
    },
    onSuccess: (data) => {
      setSaved(data.vendorPurchaseOrder);
      if (data.vendorPurchaseOrder.html) {
        setResult({
          rfqRef: data.vendorPurchaseOrder.rfqRef,
          quoteRef: null,
          qty: data.vendorPurchaseOrder.qty,
          currency: data.vendorPurchaseOrder.currency,
          vendorUnitCents: data.vendorPurchaseOrder.vendorUnitPriceCents,
          vendorTotalCents: data.vendorPurchaseOrder.vendorTotalCents,
          html: data.vendorPurchaseOrder.html,
        });
      }
      invalidate();
    },
  });

  const patch = useMutation({
    mutationFn: async (newStatus: 'sent' | 'cancelled') => {
      if (!saved) throw new Error('No saved PO to update');
      return api<PatchVendorPoResponse>(`/api/vendor-purchase-orders/${saved.id}`, {
        method: 'PATCH',
        tokenGetter: () => getToken(),
        body: JSON.stringify({ status: newStatus }),
      });
    },
    onSuccess: (data) => { setSaved(data.vendorPurchaseOrder); invalidate(); },
  });

  useEffect(() => {
    if (!result || !iframeRef.current) return;
    const doc = iframeRef.current.contentDocument;
    if (!doc) return;
    doc.open();
    doc.write(result.html);
    doc.close();
  }, [result]);

  const downloadHtml = () => {
    if (!result) return;
    const blob = new Blob([result.html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${saved?.ref ?? 'vendor-po'}.html`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  const copyHtml = async () => {
    if (!result) return;
    try { await navigator.clipboard.writeText(result.html); }
    catch { window.prompt('Copy the HTML manually:', result.html.slice(0, 100) + '…'); }
  };
  const openInNewTab = () => {
    if (!result) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.open(); win.document.write(result.html); win.document.close();
  };

  if (!open) return null;

  const previewTotal = vendorUnitPriceCents * qty;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" {...dismiss}>
      <div className="bg-background border border-border rounded-lg shadow-xl w-full max-w-5xl max-h-[94vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <h2 className="font-semibold">Generate vendor PO</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              APD → <span className="font-medium">{vendorNameRaw}</span>. Internal document — fine to name the vendor here.
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-sm" aria-label="Close">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto lg:overflow-hidden grid grid-cols-1 lg:grid-cols-[340px_1fr]">
          <aside className="border-b lg:border-r border-border overflow-auto p-4 space-y-3 bg-muted/20 text-sm">
            <div className="border border-border rounded p-3 bg-background space-y-1.5">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Vendor quote</div>
              <div className="font-medium">{vendorNameRaw}</div>
              <div className="font-mono text-xs">{fmtMoney(vendorUnitPriceCents, currency)} / ea</div>
            </div>

            <label className="block">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Quantity</div>
              <input
                type="number" min={1} step={1}
                value={qty}
                onChange={(e) => setQty(Math.max(1, Math.floor(Number(e.target.value) || 1)))}
                className="w-24 px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
              />
            </label>

            <label className="block">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Ship-to mode</div>
              <select
                value={shipToMode}
                onChange={(e) => setShipToMode(e.target.value as VendorPoShipToMode)}
                className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
              >
                <option value="customer_direct">Drop-ship direct to customer</option>
                <option value="apd_warehouse">Ship to APD</option>
              </select>
            </label>

            <label className="block">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Ship-to address</div>
              <textarea
                value={shipToText}
                onChange={(e) => setShipToText(e.target.value)}
                rows={4}
                className="w-full px-2 py-1.5 text-xs bg-background border border-border rounded font-mono"
              />
            </label>

            <label className="block">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Payment terms</div>
              <input
                type="text"
                value={paymentTerms}
                onChange={(e) => setPaymentTerms(e.target.value)}
                className="w-full px-2 py-1.5 text-xs bg-background border border-border rounded"
              />
            </label>

            <label className="block">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Certs required</div>
              <input
                type="text"
                value={certsRequired}
                onChange={(e) => setCertsRequired(e.target.value)}
                placeholder="e.g. FAA Form 8130-3 must accompany the shipment"
                className="w-full px-2 py-1.5 text-xs bg-background border border-border rounded"
              />
            </label>

            <label className="block">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Requested delivery</div>
              <input
                type="date"
                value={requestedDeliveryDate}
                onChange={(e) => setRequestedDeliveryDate(e.target.value)}
                className="w-full px-2 py-1.5 text-xs bg-background border border-border rounded"
              />
            </label>

            <label className="block">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Notes (optional)</div>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="w-full px-2 py-1.5 text-xs bg-background border border-border rounded"
              />
            </label>

            <div className="border border-border rounded p-3 bg-background text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Vendor unit</span><span className="font-mono">{fmtMoney(vendorUnitPriceCents, currency)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Qty</span><span className="font-mono">{qty}</span></div>
              <div className="flex justify-between border-t border-border pt-1.5 mt-1.5">
                <span className="font-medium">Total · {currency}</span>
                <span className="font-mono font-semibold text-primary">{fmtMoney(previewTotal, currency)}</span>
              </div>
            </div>

            {!result ? (
              <button
                onClick={() => generate.mutate()}
                disabled={generate.isPending}
                className="w-full px-3 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md disabled:opacity-50"
              >
                {generate.isPending ? 'Generating…' : 'Generate preview'}
              </button>
            ) : !saved ? (
              <div className="space-y-2">
                <div className="border border-border rounded p-3 bg-background text-xs">
                  <span className="text-muted-foreground">Preview only — not saved yet.</span>
                </div>
                <button
                  onClick={() => save.mutate()}
                  disabled={save.isPending}
                  className="w-full px-3 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md disabled:opacity-50"
                >
                  {save.isPending ? 'Saving…' : 'Save vendor PO (draft)'}
                </button>
                <button onClick={() => setResult(null)} className="w-full px-3 py-2 text-xs text-muted-foreground hover:text-foreground">← Adjust &amp; regenerate</button>
                <button onClick={downloadHtml} className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted">Download preview .html</button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="border border-border rounded p-3 bg-background">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Saved · <span className="font-semibold text-primary">{saved.status}</span></div>
                  <div className="text-sm font-mono">{saved.ref}</div>
                </div>
                {saved.status === 'draft' && (
                  <button
                    onClick={() => patch.mutate('sent')}
                    disabled={patch.isPending}
                    className="w-full px-3 py-2 text-sm font-medium bg-emerald-600 text-white rounded-md disabled:opacity-50"
                  >
                    {patch.isPending ? 'Updating…' : 'Mark as sent (advances line → po_sent)'}
                  </button>
                )}
                {saved.status === 'sent' && (
                  <button
                    onClick={() => patch.mutate('cancelled')}
                    disabled={patch.isPending}
                    className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted"
                  >
                    Mark as cancelled
                  </button>
                )}
                <div className="border-t border-border pt-2 space-y-2">
                  <button onClick={downloadHtml} className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted">Download .html</button>
                  <button onClick={copyHtml} className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted">Copy HTML to clipboard</button>
                  <button onClick={openInNewTab} className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted">Open in new tab</button>
                </div>
              </div>
            )}

            {generate.isError && <div className="text-xs text-destructive">{(generate.error as { message?: string })?.message ?? 'Generate failed'}</div>}
            {save.isError && <div className="text-xs text-destructive">{(save.error as { message?: string })?.message ?? 'Save failed'}</div>}
            {patch.isError && <div className="text-xs text-destructive">{(patch.error as { message?: string })?.message ?? 'Update failed'}</div>}
          </aside>

          <section className="overflow-hidden bg-[#f4f6fa] flex items-stretch min-h-[55vh] lg:min-h-0">
            {result ? (
              <iframe ref={iframeRef} title="Vendor PO" className="w-full h-full border-0 bg-white" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-sm text-muted-foreground px-6 text-center">
                Fill in the ship-to + terms on the left and click <span className="mx-1 font-medium">Generate preview</span>.
              </div>
            )}
          </section>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-md text-muted-foreground hover:bg-muted">Done</button>
        </div>
      </div>
    </div>
  );
}
